import React, { useState, useEffect, useRef, useContext } from 'react';
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import AlertDialog from '../components/AlertDialog';
import { FirebaseContext, api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Select,
  MenuItem,
  Grid,
  Box,
  Paper,
  Avatar,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Divider,
  Chip
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import LanguageIcon from '@mui/icons-material/Language';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import { FONT_FAMILY } from "../common/sharedFunctions";

// Yeni modern renk şeması (Sarı Tema)
const modernColors = {
  primary: '#FFC107', // Ana Sarı (Amber 500)
  primaryLight: '#FFD54F', // Açık Sarı (Amber 300)
  primaryDark: '#FFA000', // Koyu Sarı (Amber 700)
  secondary: '#10B981', // Yeşil
  secondaryLight: '#34D399',
  background: '#F9FAFB',
  surface: '#FFFFFF',
  accent: '#4F46E5', // Eski mor vurgu için kullanılabilir
  textPrimary: '#1F2937',
  textSecondary: '#4B5563',
  divider: '#E5E7EB',
  hover: '#FFFBEB',
  active: '#FFF8E1', // Aktif öğe rengi (Amber 50)
  error: '#EF4444',
  info: '#3B82F6',
  black: '#000000',
  white: '#FFFFFF',
};

const MyProfile = () => {
  const { authRef, RecaptchaVerifier, unlink, linkWithPhoneNumber } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    updateProfile,
    updateWebProfileImage,
    updateProfileWithEmail,
    checkUserExists,
    requestMobileOtp,
    updateAuthMobile
  } = api;
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const languagedata = useSelector(state => state.languagedata);
  const [langSelection, setLangSelection] = useState(0);
  const [multiLanguage, setMultiLanguage] = useState();
  const fileInputRef = useRef();
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const settings = useSelector(state => state.settingsdata.settings);

  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    usertype: '',
    verifyId: '',
    profile_image: '',
  });

  const [otp, setOtp] = useState("");
  const [otpCalled, setOtpCalled] = useState(false);
  const [updateCalled, setUpdateCalled] = useState(false);
  const [confirmCodeFunction, setConfirmCodeFunction] = useState();

  useEffect(() => {
    if (languagedata.langlist) {
      let arr = Object.keys(languagedata.langlist);
      for (let i = 0; i < arr.length; i++) {
        if (auth.profile && auth.profile.lang && auth.profile.lang.langLocale && auth.profile.lang.langLocale === languagedata.langlist[arr[i]].langLocale) {
          setLangSelection(i);
          break; // Dili bulduktan sonra döngüden çık
        }
      }
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist, auth.profile]);

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setData({
        firstName: auth.profile.firstName || '',
        lastName: auth.profile.lastName || '',
        email: auth.profile.email || '',
        mobile: auth.profile.mobile || '',
        usertype: auth.profile.usertype || '',
        profile_image: auth.profile.profile_image || '',
        verifyId: auth.profile.verifyId || ''
      });
      if (updateCalled) {
        setLoading(false);
        setCommonAlert({ open: true, msg: t('profile_updated') });
        setUpdateCalled(false);
      }
    }
  }, [auth.profile, t, updateCalled]);

  const updateData = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  }

  const completeSubmit = () => {
    setUpdateCalled(true);
    let arr = Object.keys(languagedata.langlist);
    const selectedLangData = multiLanguage && multiLanguage[arr[langSelection]]
      ? { langLocale: multiLanguage[arr[langSelection]].langLocale, dateLocale: multiLanguage[arr[langSelection]].dateLocale }
      : null;

    if (selectedLangData) {
      localStorage.setItem('lang', JSON.stringify(selectedLangData));
    }

    dispatch(updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      mobile: data.mobile,
      verifyId: data.verifyId,
      lang: selectedLangData
    }));

    if (selectedImage) {
      updateWebProfileImage(selectedImage);
    }

    if (selectedLangData) {
      const lang = multiLanguage[arr[langSelection]];
      i18n.addResourceBundle(lang.langLocale, 'translations', lang.keyValuePairs);
      i18n.changeLanguage(lang.langLocale);
      moment.locale(lang.dateLocale);
    }
    setSelectedImage(null);
    setCommonAlert({ open: true, msg: t('profile_updated') });
    setLoading(false); // Yükleme durumunu bitir
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Yüklemeyi başlat

    if (data.email !== auth.profile.email && data.mobile !== auth.profile.mobile) {
      setLoading(false);
      setCommonAlert({ open: true, msg: t('update_any') })
      return;
    }

    if (!data.firstName || data.firstName.length === 0 || !data.lastName || data.lastName.length === 0) {
       setLoading(false);
       setCommonAlert({ open: true, msg: t('no_details_error') });
       return;
    }

    try {
        if (data.email !== auth.profile.email) {
          const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          if (!re.test(data.email)) {
            setLoading(false);
            setCommonAlert({ open: true, msg: t('proper_email') });
            return;
          }
          const res = await checkUserExists({ email: data.email });
          if (res.users && res.users.length > 0) {
             setLoading(false);
             alert(t('user_exists'));
             return;
          } else if (res.error) {
             setLoading(false);
             alert(t('email_or_mobile_issue'));
             return;
          }
          data['uid'] = auth.profile.uid;
          dispatch(updateProfileWithEmail(data)); // Bu async olmayabilir, kontrol et
          completeSubmit(); // E-posta güncellendiyse de completeSubmit'i çağır

        } else if (data.mobile !== auth.profile.mobile) {
          if (!data.mobile || data.mobile.length <= 6) {
            setLoading(false);
            setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
            return;
          }
          const res = await checkUserExists({ mobile: data.mobile });
           if (res.users && res.users.length > 0) {
              setLoading(false);
              alert(t('user_exists'));
              return;
           } else if (res.error) {
              setLoading(false);
              alert(t('email_or_mobile_issue'));
              return;
           }
           setOtpCalled(true);
           if (settings.customMobileOTP) {
             dispatch(requestMobileOtp(data.mobile)); // Bu async olmayabilir
           } else {
             window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', { 'size': 'invisible' });
             if (auth.profile.mobile && auth.profile.mobile.length > 6) {
               await unlink(authRef().currentUser, "phone");
             }
             const linkObj = await linkWithPhoneNumber(authRef().currentUser, data.mobile, window.recaptchaVerifier);
             setConfirmCodeFunction(() => linkObj); // Fonksiyonu state'e atarken callback kullan
           }
           setLoading(false); // OTP gönderildikten sonra yüklemeyi durdur

        } else {
          // Sadece diğer bilgiler güncelleniyorsa
          completeSubmit();
        }
    } catch (error) {
        console.error("Submit Error:", error);
        setLoading(false);
        setCommonAlert({ open: true, msg: t('generic_error') }); // Genel hata mesajı
    }
  }

  const updateOtp = (e) => {
    setOtp(e.target.value);
  }

  const handleVerify = async () => {
    if (!otp || otp.length !== 6 || isNaN(otp)) {
       setOtp('');
       setCommonAlert({ open: true, msg: t('otp_validate_error') });
       return;
    }
    setLoading(true);
    setOtpCalled(false); // Dialog'u kapatmak için
    try {
      if (settings.customMobileOTP) {
        const res = await updateAuthMobile(data.mobile, otp);
        if (res.success) {
          completeSubmit();
        } else {
          setOtp('');
          setLoading(false);
          if (res.error === 'Error updating user') {
            setCommonAlert({ open: true, msg: t('user_exists') })
          } else {
            setCommonAlert({ open: true, msg: t('otp_validate_error') })
          }
        }
      } else {
        if (confirmCodeFunction) {
          await confirmCodeFunction.confirm(otp);
          completeSubmit();
        } else {
           setLoading(false);
           setCommonAlert({ open: true, msg: t('generic_error') });
        }
      }
    } catch(error) {
        console.error("OTP Verify Error:", error);
        setOtp('');
        setLoading(false);
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear();
        }
        setCommonAlert({ open: true, msg: t('otp_validate_error') })
    } finally {
       if (window.recaptchaVerifier) {
         window.recaptchaVerifier = null;
       }
    }
  }

  const handleClose = () => {
    setOtp('');
    setOtpCalled(false);
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  }

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };

  const handleLanguageSelect = (event) => {
    setLangSelection(event.target.value);
  };

  const profileImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
       setSelectedImage(e.target.files[0]);
    }
  }

  return (
    auth.loading ? <CircularLoading /> :
      <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 4, fontFamily: FONT_FAMILY }}>
        <CssBaseline />
        <div id="sign-in-button" />
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '16px',
            backgroundColor: modernColors.surface,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700, color: modernColors.textPrimary, mb: 3, textAlign: 'center', fontFamily: FONT_FAMILY }}>
            {t('profile_title')}
          </Typography>

          <Grid container spacing={4}>
            {/* Sol Sütun: Avatar ve Temel Bilgiler */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', textAlign: 'center', backgroundColor: modernColors.active, /* Sarı aktif arka plan */ height: '100%' }}>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <Avatar
                    src={selectedImage ? URL.createObjectURL(selectedImage) : data.profile_image || require("../assets/img/profilePic.png").default}
                    alt="Profile"
                    sx={{
                      width: 150,
                      height: 150,
                      margin: 'auto',
                      border: `4px solid ${modernColors.primary}`, // Ana sarı border
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      cursor: 'pointer'
                    }}
                    onClick={() => fileInputRef.current.click()}
                  />
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="span"
                    onClick={() => fileInputRef.current.click()}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: modernColors.surface,
                      color: modernColors.primary, // Ana sarı
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      '&:hover': {
                        backgroundColor: modernColors.hover, // Açık sarı hover
                      }
                    }}
                  >
                    <PhotoCamera />
                  </IconButton>
                  <input onChange={profileImageChange} multiple={false} ref={fileInputRef} type='file' accept="image/*" hidden />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: modernColors.textPrimary, fontFamily: FONT_FAMILY, mb: 0.5 }}>
                  {data.firstName} {data.lastName}
                </Typography>
                <Typography variant="body1" sx={{ color: modernColors.textSecondary, fontFamily: FONT_FAMILY, mb: 2 }}>
                  {data.email}
                </Typography>
                <Chip label={data.usertype} sx={{ backgroundColor: modernColors.primary, /* Sarı chip */ color: modernColors.textPrimary, /* Sarı ile koyu text */ fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </Paper>
            </Grid>

            {/* Sağ Sütun: Form */}
            <Grid item xs={12} md={8}>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="firstName"
                      label={t('firstname')}
                      name="firstName"
                      autoComplete="given-name"
                      onChange={updateData}
                      value={data.firstName}
                      autoFocus
                      InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: modernColors.textSecondary }} /></InputAdornment> }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.divider }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primaryLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primary } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="lastName"
                      label={t('lastname')}
                      name="lastName"
                      autoComplete="family-name"
                      onChange={updateData}
                      value={data.lastName}
                       InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: modernColors.textSecondary }} /></InputAdornment> }}
                       sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.divider }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primaryLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primary } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="email"
                      label={t('email')}
                      name="email"
                      autoComplete="email"
                      onChange={updateData}
                      value={data.email}
                      InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: modernColors.textSecondary }} /></InputAdornment> }}
                       sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="mobile"
                      label={t('phone')}
                      name="mobile"
                      autoComplete="tel"
                      onChange={updateData}
                      value={data.mobile}
                      InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: modernColors.textSecondary }} /></InputAdornment> }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                      dir="ltr" // Ensure LTR for phone number input
                    />
                  </Grid>
                  {((settings && settings.imageIdApproval) || (data && data.verifyId)) && auth.profile.usertype !== 'admin' ? (
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        id="verifyId"
                        label={t('verify_id')}
                        name="verifyId"
                        value={data.verifyId}
                        onChange={updateData}
                        InputProps={{ startAdornment: <InputAdornment position="start"><VerifiedUserIcon sx={{ color: modernColors.textSecondary }} /></InputAdornment> }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                      />
                    </Grid>
                  ) : null}
                   <Grid item xs={12}>
                     <Divider sx={{ my: 1 }} />
                   </Grid>

                  {multiLanguage && multiLanguage.length > 1 ? (
                    <Grid item xs={12} sm={6}>
                       <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                           <InputLabel id="language-select-label">{t('language')}</InputLabel>
                            <Select
                              labelId="language-select-label"
                              id="language-select"
                              value={langSelection}
                              label={t('language')}
                              onChange={handleLanguageSelect}
                              startAdornment={<InputAdornment position="start"><LanguageIcon sx={{ color: modernColors.textSecondary, mr:1 }} /></InputAdornment>}
                             >
                                {Object.keys(multiLanguage).map((key, index) => (
                                   <MenuItem key={key} value={index} sx={{ justifyContent: isRTL === 'rtl' ? "flex-end" : "flex-start", fontFamily: FONT_FAMILY }}>
                                     {multiLanguage[key].langName}
                                   </MenuItem>
                                ))}
                           </Select>
                       </FormControl>
                    </Grid>
                  ) : null}

                   <Grid item xs={12} sm={multiLanguage && multiLanguage.length > 1 ? 6 : 12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                     <Button
                       type="submit"
                       variant="contained"
                       color="primary" // MUI'nin primary'sini kullanır, sx ile override ederiz
                       disabled={loading}
                       startIcon={loading ? <CircularProgress size={20} sx={{ color: modernColors.textPrimary }} /* Sarı buton üstünde koyu ikon */ /> : <SaveIcon />}
                       sx={{
                         py: 1.5,
                         px: 4,
                         borderRadius: '10px',
                         backgroundColor: modernColors.primary, // Ana sarı
                         color: modernColors.textPrimary, // Sarı üstünde koyu text
                         fontFamily: FONT_FAMILY,
                         fontWeight: 600,
                         textTransform: 'none',
                         fontSize: '1rem',
                         '&:hover': {
                           backgroundColor: modernColors.primaryDark, // Koyu sarı hover
                           boxShadow: `0 4px 12px ${modernColors.primary}40`,
                         }
                       }}
                     >
                       {loading ? t('saving') : t('save_changes')}
                     </Button>
                   </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>

        <Dialog open={otpCalled} onClose={handleClose} PaperProps={{ sx: { borderRadius: '12px' } }}>
          <DialogTitle sx={{ fontWeight: 600, fontFamily: FONT_FAMILY, color: modernColors.textPrimary, borderBottom: `1px solid ${modernColors.divider}` }}>{t('enter_otp')}</DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
             <Typography sx={{ mb: 2, color: modernColors.textSecondary, fontFamily: FONT_FAMILY }}>
               {t('otp_sent_to')} {data.mobile}
             </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="otp"
              label={t('otp')}
              type="tel"
              fullWidth
              variant="outlined"
              onChange={updateOtp}
              value={otp}
              inputProps={{ maxLength: 6 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><VpnKeyIcon sx={{ color: modernColors.textSecondary }} /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.divider }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primaryLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primary } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${modernColors.divider}` }}>
             {loading ? (
                <CircularProgress size={24} sx={{ color: modernColors.primary }}/>
             ) : (
                <>
                 <Button onClick={handleClose} sx={{ color: modernColors.textSecondary, fontFamily: FONT_FAMILY, textTransform: 'none' }}>{t('cancel')}</Button>
                 <Button onClick={handleVerify} variant="contained" sx={{ backgroundColor: modernColors.primary, color: modernColors.textPrimary, fontFamily: FONT_FAMILY, borderRadius: '8px', textTransform: 'none', '&:hover': { backgroundColor: modernColors.primaryDark } }}>{t('verify_otp')}</Button>
                </>
             )}
          </DialogActions>
        </Dialog>
      </Container>
  );
};

export default MyProfile;